import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  ArrowLeftIcon,
  SparklesIcon,
  BanknotesIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  EllipsisVerticalIcon,
  EyeIcon,
  PencilSquareIcon,
  PhoneIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { clientsAPI, familyMembersAPI, reportsAPI } from '../services/api';
import { LoadingSpinner, ConfirmModal, Button, BottomSheet } from '../components/ui';
import { ClientProfileHeader } from '../components/client-profile/ClientProfileHeader';
import { ClientSummaryStats } from '../components/client-profile/ClientSummaryStats';
import { ClientPersonalInfoCard } from '../components/client-profile/ClientPersonalInfoCard';
import { ClientFamilySection } from '../components/client-profile/ClientFamilySection';
import { ClientPlanSection } from '../components/client-profile/ClientPlanSection';
import { FamilyMemberModal, type FamilyMemberFormData } from '../components/client-profile/FamilyMemberModal';
import {
  normalizeClientPayload,
  computeFinancialSummary,
  groupFamilyMembers,
  profileAvatarClass,
  profileInitials,
} from '../components/client-profile/clientProfileUtils';
import { deriveClientStatus } from '../components/clients/clientStatus';
import { ClientStatusBadge } from '../components/clients/ClientStatusBadge';
import type { Client, FamilyMember } from '../types';
import { applyDominicanIDMask, calculateAge } from '../utils/validation';
import { getInvoiceStatusLabel } from '../utils/invoiceStatus';
import { getPaymentMethodLabel } from '../utils/paymentLabels';
import toast from 'react-hot-toast';

type DetailTab =
  | 'resumen'
  | 'familiares'
  | 'pagos'
  | 'facturas'
  | 'planes'
  | 'historial'
  | 'servicios';

const TABS: { id: DetailTab; label: string }[] = [
  { id: 'resumen', label: 'Resumen' },
  { id: 'familiares', label: 'Familiares' },
  { id: 'pagos', label: 'Pagos' },
  { id: 'facturas', label: 'Facturas' },
  { id: 'planes', label: 'Planes' },
  { id: 'historial', label: 'Historial' },
  { id: 'servicios', label: 'Servicios funerarios' }
];

function PlaceholderPanel({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50/60 px-6 py-16 text-center">
      <SparklesIcon className="mx-auto h-10 w-10 text-slate-300" aria-hidden />
      <p className="mt-3 text-base font-semibold text-slate-800">{title}</p>
      <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">
        {hint ||
          'Módulo preparado para conectar listados, filtros y acciones cuando la información esté disponible en backend.'}
      </p>
    </div>
  );
}

const ClientDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<DetailTab>('resumen');
  const [moreOpen, setMoreOpen] = useState(false);
  const [isFamilyModalOpen, setIsFamilyModalOpen] = useState(false);
  const [selectedFamilyMember, setSelectedFamilyMember] = useState<FamilyMember | null>(null);
  const [familyConfirm, setFamilyConfirm] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'warning' | 'danger' | 'success' | 'info';
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'warning',
    onConfirm: () => {}
  });

  const { data, isLoading, error, isError } = useQuery(
    ['client', id],
    async () => {
      const res = await clientsAPI.getById(id as string);
      return res.data.data;
    },
    { enabled: Boolean(id), retry: 1 }
  );

  useEffect(() => {
    const incoming = (location.state as { tab?: DetailTab } | null)?.tab;
    if (incoming && TABS.some((t) => t.id === incoming)) {
      setTab(incoming);
    }
  }, [location.state]);

  const client = useMemo(() => normalizeClientPayload(data as Record<string, unknown> | undefined), [data]);

  const groupedFamilyMembers = useMemo(() => groupFamilyMembers(client), [client]);
  const totalFamilyMembers = useMemo(
    () => Object.values(groupedFamilyMembers).reduce((s, g) => s + g.length, 0),
    [groupedFamilyMembers]
  );

  const financial = useMemo(
    () => (client ? computeFinancialSummary(client as Parameters<typeof computeFinancialSummary>[0]) : null),
    [client]
  );

  const paymentsSorted = useMemo(() => {
    const raw = (client as Client & { payments?: { amount?: number; paymentDate?: string; id?: string }[] })?.payments;
    if (!raw?.length) return [];
    return [...raw].sort(
      (a, b) => new Date(b.paymentDate || 0).getTime() - new Date(a.paymentDate || 0).getTime()
    );
  }, [client]);

  const invoicesFlat = useMemo(() => {
    const plans = client?.plans || [];
    const list: { id: string | number; status?: string; amountDue?: number; dueDate?: string }[] = [];
    for (const p of plans as { invoices?: typeof list }[]) {
      for (const inv of p.invoices || []) {
        list.push(inv);
      }
    }
    return list;
  }, [client]);

  const createFamilyMutation = useMutation(familyMembersAPI.create, {
    onSuccess: () => {
      queryClient.invalidateQueries(['client', id]);
      queryClient.invalidateQueries('clients');
      toast.success('Familiar agregado exitosamente');
      setIsFamilyModalOpen(false);
      setSelectedFamilyMember(null);
    },
    onError: (err: { response?: { data?: { error?: string } } }) => {
      toast.error(err.response?.data?.error || 'Error al agregar el familiar');
    }
  });

  const updateFamilyMutation = useMutation(
    ({ fid, payload }: { fid: string; payload: FamilyMemberFormData }) => familyMembersAPI.update(fid, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['client', id]);
        queryClient.invalidateQueries('clients');
        toast.success('Familiar actualizado exitosamente');
        setIsFamilyModalOpen(false);
        setSelectedFamilyMember(null);
      },
      onError: (err: { response?: { data?: { error?: string } } }) => {
        toast.error(err.response?.data?.error || 'Error al actualizar el familiar');
      }
    }
  );

  const deleteFamilyMutation = useMutation(familyMembersAPI.delete, {
    onSuccess: () => {
      queryClient.invalidateQueries(['client', id]);
      queryClient.invalidateQueries('clients');
      toast.success('Familiar eliminado exitosamente');
    },
    onError: (err: { response?: { data?: { error?: string } } }) => {
      toast.error(err.response?.data?.error || 'Error al eliminar el familiar');
    }
  });

  const handleSaveFamilyMember = (form: FamilyMemberFormData) => {
    if (selectedFamilyMember) {
      updateFamilyMutation.mutate({ fid: selectedFamilyMember.id, payload: form });
    } else {
      createFamilyMutation.mutate({ ...form, clientId: id || '' });
    }
  };

  const handleDeleteFamilyMember = (fid: string) => {
    setFamilyConfirm({
      isOpen: true,
      title: 'Eliminar Familiar',
      message: '¿Está seguro de que desea eliminar este familiar?',
      type: 'danger',
      onConfirm: () => {
        deleteFamilyMutation.mutate(fid);
        setFamilyConfirm((p) => ({ ...p, isOpen: false }));
      }
    });
  };

  const goEditClient = () => {
    if (!id) return;
    navigate('/clients', { state: { editClientId: id } });
  };

  const handleDownloadStatement = async () => {
    if (!id) return;
    try {
      const response = await reportsAPI.getStatement(id);
      const statement = response.data?.data || response.data;
      const clientName = `${client?.name || ''} ${client?.lastname || ''}`.trim();
      const date = new Date().toLocaleDateString('es');
      const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Estado de Cuenta</title>
        <style>body{font-family:sans-serif;max-width:700px;margin:0 auto;padding:20px;color:#1e293b}
        h1{font-size:1.25rem;font-weight:700;margin-bottom:4px}
        .sub{color:#64748b;font-size:.875rem;margin-bottom:20px}
        table{width:100%;border-collapse:collapse;margin-top:12px}
        th{text-align:left;font-size:.75rem;font-weight:700;text-transform:uppercase;color:#64748b;padding:8px 12px;border-bottom:2px solid #e2e8f0}
        td{padding:8px 12px;border-bottom:1px solid #f1f5f9;font-size:.875rem}
        .right{text-align:right}.mono{font-family:monospace}</style>
        </head><body>
        <h1>Estado de Cuenta — ${clientName}</h1>
        <p class="sub">Cédula: ${client?.cedula || '—'} &nbsp;|&nbsp; Generado: ${date}</p>
        ${Array.isArray(statement?.invoices) ? `
        <table><thead><tr><th>Factura</th><th>Estado</th><th class="right">Total</th><th class="right">Saldo</th><th>Vencimiento</th></tr></thead>
        <tbody>${statement.invoices.map((inv: any) => `<tr>
          <td>${inv.invoiceNumber || `#${inv.id}`}</td>
          <td>${getInvoiceStatusLabel(inv.status)}</td>
          <td class="right mono">$${Number(inv.amountDue || 0).toFixed(2)}</td>
          <td class="right mono">$${inv.balanceDueMinor != null ? (inv.balanceDueMinor / 100).toFixed(2) : Number(inv.amountDue || 0).toFixed(2)}</td>
          <td>${inv.dueDate ? new Date(inv.dueDate).toLocaleDateString('es') : '—'}</td>
        </tr>`).join('')}</tbody></table>` : '<p>Sin facturas disponibles.</p>'}
        </body></html>`;
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const win = window.open(url, '_blank');
      if (win) win.focus();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('No se pudo generar el estado de cuenta');
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isError || error || !client) {
    return (
      <div className="mx-auto max-w-lg rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
        <p className="font-semibold text-slate-900">No se pudo cargar el cliente</p>
        <p className="mt-2 text-sm text-slate-600">Verifique el enlace o vuelva al directorio.</p>
        <Link
          to="/clients"
          className="mt-6 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Volver a clientes
        </Link>
      </div>
    );
  }

  const status = deriveClientStatus(client);
  const cedulaDisplay =
    applyDominicanIDMask(String(client.cedula || '').replace(/\D/g, '')) || client.cedula;
  const gremio = (client.gremio || '').trim() || '—';
  const primaryPhone = client.telefonos?.split(',')[0]?.trim() || '';
  const planLabel = (() => {
    const plans = client.plans || [];
    if (!plans.length) return 'Sin plan';
    const p = plans[0] as { planTemplate?: { name?: string }; name?: string; status?: string };
    return (p.planTemplate as { name?: string } | undefined)?.name || p.name || 'Plan';
  })();
  const planActive = (() => {
    const plans = client.plans || [];
    if (!plans.length) return false;
    return String((plans[0] as { status?: string }).status || 'active').toLowerCase() === 'active';
  })();

  return (
    <div className="animate-fade-in-up space-y-4 pb-16 md:space-y-6">
      {/* Desktop top bar */}
      <div className="hidden flex-col gap-3 sm:flex-row sm:items-center sm:justify-between md:flex">
        <Link
          to="/clients"
          className="inline-flex w-fit items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-900"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Volver al directorio
        </Link>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleDownloadStatement}
          className="inline-flex items-center gap-1.5"
        >
          <DocumentTextIcon className="h-4 w-4" />
          Estado de cuenta
        </Button>
      </div>

      {/* Mobile profile header */}
      <div className="space-y-4 md:hidden">
        <div className="flex items-center justify-between">
          <Link
            to="/clients"
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl text-slate-600 transition hover:bg-slate-100 active:scale-95"
            aria-label="Volver"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl text-slate-600 transition hover:bg-slate-100 active:scale-95"
            aria-label="Más acciones"
          >
            <EllipsisVerticalIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-col items-center text-center">
          <div
            className={clsx(
              'flex h-16 w-16 items-center justify-center rounded-2xl text-xl font-bold ring-2 ring-white',
              profileAvatarClass(`${client.name}${client.lastname}${client.id}`)
            )}
          >
            {profileInitials(client.name, client.lastname)}
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
            <h1 className="text-xl font-bold text-slate-900">
              {client.name} {client.lastname}
            </h1>
            {status === 'moroso' ? (
              <span className="rounded-md border border-[#FECDD3] bg-[#FFF1F2] px-2 py-0.5 text-[11px] font-semibold text-[#BE123C]">
                Moroso
              </span>
            ) : (
              <ClientStatusBadge status={status} className="!px-2 !py-0.5 !text-[11px]" />
            )}
          </div>
          <p className="mt-1 text-sm font-medium text-primary-600">{planLabel}</p>
          {planActive ? (
            <p className="mt-1 inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              Plan activo
            </p>
          ) : null}
        </div>

        <div className="grid grid-cols-3 gap-2">
          <div className="rounded-2xl border border-[#E5EAF1] bg-white px-2 py-3 text-center shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Cédula</p>
            <p className="mt-1 truncate font-mono text-[11px] font-semibold text-slate-900">{cedulaDisplay}</p>
          </div>
          <div className="rounded-2xl border border-[#E5EAF1] bg-white px-2 py-3 text-center shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Gremio</p>
            <p className="mt-1 truncate text-[11px] font-semibold text-slate-900">{gremio}</p>
          </div>
          <div className="rounded-2xl border border-[#E5EAF1] bg-white px-2 py-3 text-center shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Familiares</p>
            <p className="mt-1 text-[11px] font-semibold tabular-nums text-slate-900">{totalFamilyMembers}</p>
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-[#E5EAF1] bg-white">
          <div className="border-b border-[#E5EAF1] px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Dirección</p>
            <p className="mt-1 text-sm font-medium text-slate-900">{client.address || '—'}</p>
          </div>
          <div className="border-b border-[#E5EAF1] px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Teléfono</p>
            <div className="mt-1 flex items-center justify-between gap-2">
              <p className="text-sm font-medium text-slate-900">{primaryPhone || '—'}</p>
              {primaryPhone ? (
                <div className="flex items-center gap-1.5">
                  <a
                    href={`tel:${primaryPhone.replace(/\D/g, '')}`}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700"
                    aria-label="Llamar"
                  >
                    <PhoneIcon className="h-4 w-4" />
                  </a>
                  <a
                    href={`https://wa.me/${primaryPhone.replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex h-8 items-center rounded-lg bg-emerald-50 px-2 text-[11px] font-semibold text-emerald-700"
                  >
                    WhatsApp
                  </a>
                </div>
              ) : null}
            </div>
          </div>
          <div className="border-b border-[#E5EAF1] px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Email</p>
            <p className="mt-1 text-sm font-medium text-slate-900">
              {(client as { email?: string }).email || '—'}
            </p>
          </div>
          <div className="px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Notas</p>
            <p className="mt-1 text-sm font-medium text-slate-700">
              {(client as { notes?: string; observacion?: string }).notes ||
                (client as { observacion?: string }).observacion ||
                'Sin notas administrativas.'}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <button
            type="button"
            onClick={() => setTab('resumen')}
            className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-[#E5EAF1] bg-white px-2 py-2.5 text-xs font-semibold text-slate-700 transition active:scale-95"
          >
            <EyeIcon className="h-4 w-4" />
            Detalles
          </button>
          <button
            type="button"
            onClick={goEditClient}
            className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-[#E5EAF1] bg-white px-2 py-2.5 text-xs font-semibold text-slate-700 transition active:scale-95"
          >
            <PencilSquareIcon className="h-4 w-4" />
            Editar
          </button>
          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-[#E5EAF1] bg-white px-2 py-2.5 text-xs font-semibold text-slate-700 transition active:scale-95"
          >
            <EllipsisVerticalIcon className="h-4 w-4" />
            Más
          </button>
        </div>
      </div>

      {/* Desktop profile header */}
      <div className="hidden md:block">
        <ClientProfileHeader
          variant="page"
          client={client}
          onEdit={goEditClient}
          onRegisterPayment={() => {
            navigate(`/cobros?clientId=${client.id}&tab=payments`);
          }}
          onViewInvoices={() => {
            navigate(`/cobros?clientId=${client.id}&tab=invoices`);
          }}
          onAddFamily={() => {
            setSelectedFamilyMember(null);
            setIsFamilyModalOpen(true);
            setTab('familiares');
          }}
          onViewHistory={() => {
            setTab('historial');
          }}
        />
      </div>

      <div className="overflow-x-auto border-b border-slate-200/90">
        <nav className="flex min-w-max gap-1 pb-px" aria-label="Secciones del cliente">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={clsx(
                'whitespace-nowrap rounded-t-xl px-4 py-3 text-sm font-semibold transition',
                tab === t.id
                  ? 'bg-white text-slate-900 ring-1 ring-slate-200 ring-b-white'
                  : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800'
              )}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'resumen' && (
        <div className="space-y-6">
          {financial && (
            <div className="hidden md:block">
              <ClientSummaryStats
                client={client}
                financial={financial}
                familyCount={totalFamilyMembers}
                showServicesPlaceholder
              />
            </div>
          )}
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="hidden md:block">
              <ClientPersonalInfoCard client={client} />
            </div>
            <ClientPlanSection client={client} />
          </div>
          <ClientFamilySection
            client={client}
            totalCount={totalFamilyMembers}
            grouped={groupedFamilyMembers}
            onAdd={() => {
              setSelectedFamilyMember(null);
              setIsFamilyModalOpen(true);
            }}
            onEdit={(fm) => {
              setSelectedFamilyMember(fm);
              setIsFamilyModalOpen(true);
            }}
            onDelete={handleDeleteFamilyMember}
            calculateAge={calculateAge}
          />
        </div>
      )}

      {tab === 'familiares' && (
        <ClientFamilySection
          client={client}
          totalCount={totalFamilyMembers}
          grouped={groupedFamilyMembers}
          onAdd={() => {
            setSelectedFamilyMember(null);
            setIsFamilyModalOpen(true);
          }}
          onEdit={(fm) => {
            setSelectedFamilyMember(fm);
            setIsFamilyModalOpen(true);
          }}
          onDelete={handleDeleteFamilyMember}
          calculateAge={calculateAge}
        />
      )}

      {tab === 'pagos' && (
        <div className="space-y-4">
          {paymentsSorted.length === 0 ? (
            <PlaceholderPanel title="Sin pagos registrados" hint="Los cobros asociados a este cliente aparecerán aquí." />
          ) : (
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <table className="min-w-full divide-y divide-slate-100 text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-600">Fecha</th>
                    <th className="px-4 py-3 text-right text-xs font-bold uppercase text-slate-600">Monto</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {paymentsSorted.map((p) => (
                    <tr key={p.id} className="hover:bg-slate-50/80">
                      <td className="px-4 py-3 text-slate-800">
                        {p.paymentDate
                          ? new Date(p.paymentDate).toLocaleString('es', {
                              dateStyle: 'medium',
                              timeStyle: 'short'
                            })
                          : '—'}
                      </td>
                      <td className="px-4 py-3 text-right font-mono font-semibold text-slate-900">
                        ${Number(p.amount || 0).toLocaleString('es', { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'facturas' && (
        <div className="space-y-4">
          {invoicesFlat.length === 0 ? (
            <PlaceholderPanel title="Sin facturas vinculadas" hint="Las facturas del plan del cliente se listarán aquí." />
          ) : (
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <table className="min-w-full divide-y divide-slate-100 text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-600">Estado</th>
                    <th className="px-4 py-3 text-right text-xs font-bold uppercase text-slate-600">Monto</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-600">Vencimiento</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {invoicesFlat.map((inv) => (
                    <tr key={String(inv.id)} className="hover:bg-slate-50/80">
                      <td className="px-4 py-3 font-medium text-slate-800">{getInvoiceStatusLabel(inv.status)}</td>
                      <td className="px-4 py-3 text-right font-mono">
                        ${Number(inv.amountDue || 0).toLocaleString('es', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {inv.dueDate ? new Date(inv.dueDate).toLocaleDateString('es') : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'planes' && <ClientPlanSection client={client} />}

      {tab === 'historial' && (() => {
        // Build chronological activity from payments + invoice events
        type ActivityEvent = {
          id: string;
          date: Date;
          type: 'payment' | 'invoice' | 'plan';
          title: string;
          subtitle: string;
          amount?: number;
          status?: string;
        };
        const events: ActivityEvent[] = [];

        // Payments
        for (const p of (paymentsSorted as any[])) {
          events.push({
            id: `pay-${p.id}`,
            date: new Date(p.paymentDate || p.createdAt || 0),
            type: 'payment',
            title: 'Pago registrado',
            subtitle: getPaymentMethodLabel(p.paymentMethod),
            amount: p.amount || (p.amountMinor ? p.amountMinor / 100 : 0),
            status: p.status
          });
        }

        // Invoices from plans
        for (const inv of (invoicesFlat as any[])) {
          events.push({
            id: `inv-${inv.id}`,
            date: new Date(inv.dueDate || inv.createdAt || 0),
            type: 'invoice',
            title: `Factura ${inv.invoiceNumber || `#${inv.id}`}`,
            subtitle: getInvoiceStatusLabel(inv.status || 'DRAFT'),
            amount: inv.amountDue || 0,
            status: inv.status
          });
        }

        events.sort((a, b) => b.date.getTime() - a.date.getTime());

        if (events.length === 0) {
          return (
            <PlaceholderPanel
              title="Sin actividad registrada"
              hint="Los pagos y facturas del cliente aparecerán aquí en orden cronológico."
            />
          );
        }

        const iconMap = {
          payment: BanknotesIcon,
          invoice: DocumentTextIcon,
          plan: CheckCircleIcon,
        };
        const colorMap: Record<string, string> = {
          payment: 'bg-emerald-100 text-emerald-700',
          invoice: 'bg-blue-100 text-blue-700',
          plan: 'bg-primary-100 text-primary-700',
        };
        const statusColor: Record<string, string> = {
          PAID: 'text-emerald-700',
          CONFIRMED: 'text-emerald-700',
          OVERDUE: 'text-rose-600',
          CANCELLED: 'text-slate-400',
          PENDING: 'text-amber-600',
          PARTIAL: 'text-amber-600',
          ISSUED: 'text-blue-600',
        };

        return (
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/60">
              <h3 className="text-sm font-semibold text-slate-800">Línea de tiempo</h3>
              <p className="text-xs text-slate-500 mt-0.5">{events.length} eventos registrados</p>
            </div>
            <ul className="divide-y divide-slate-100">
              {events.map(ev => {
                const Icon = iconMap[ev.type];
                return (
                  <li key={ev.id} className="flex items-start gap-4 px-6 py-4 hover:bg-slate-50/60 transition-colors">
                    <div className={`mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl ${colorMap[ev.type]}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold text-slate-900">{ev.title}</span>
                        {ev.amount != null && ev.amount > 0 && (
                          <span className="text-sm font-mono font-semibold text-slate-800">
                            ${Number(ev.amount).toLocaleString('es', { minimumFractionDigits: 2 })}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className={`text-xs font-medium ${ev.status ? (statusColor[ev.status] || 'text-slate-500') : 'text-slate-500'}`}>
                          {ev.subtitle}
                        </span>
                        <span className="text-slate-300">·</span>
                        <span className="text-xs text-slate-400">
                          {ev.date.toLocaleString('es', { dateStyle: 'medium', timeStyle: 'short' })}
                        </span>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })()}

      {tab === 'servicios' && (
        <PlaceholderPanel
          title="Servicios funerarios"
          hint="Activaciones, servicios en curso y cierres operativos se mostrarán en esta pestaña."
        />
      )}

      <FamilyMemberModal
        isOpen={isFamilyModalOpen}
        onClose={() => setIsFamilyModalOpen(false)}
        familyMember={selectedFamilyMember}
        clientId={String(client.id)}
        client={client}
        onSave={handleSaveFamilyMember}
        isLoading={createFamilyMutation.isLoading || updateFamilyMutation.isLoading}
      />

      <ConfirmModal
        isOpen={familyConfirm.isOpen}
        onClose={() => setFamilyConfirm((p) => ({ ...p, isOpen: false }))}
        onConfirm={familyConfirm.onConfirm}
        title={familyConfirm.title}
        message={familyConfirm.message}
        type={familyConfirm.type}
        isLoading={deleteFamilyMutation.isLoading}
      />

      <BottomSheet open={moreOpen} onClose={() => setMoreOpen(false)} title="Más acciones">
        <div className="space-y-0.5 pb-2">
          <button
            type="button"
            className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-medium text-slate-800 hover:bg-slate-50"
            onClick={() => {
              setMoreOpen(false);
              navigate(`/cobros?clientId=${client.id}&tab=payments`);
            }}
          >
            <BanknotesIcon className="h-5 w-5 text-slate-400" />
            Registrar pago
          </button>
          <button
            type="button"
            className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-medium text-slate-800 hover:bg-slate-50"
            onClick={() => {
              setMoreOpen(false);
              navigate(`/cobros?clientId=${client.id}&tab=invoices`);
            }}
          >
            <DocumentTextIcon className="h-5 w-5 text-slate-400" />
            Ver facturas
          </button>
          <button
            type="button"
            className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-medium text-slate-800 hover:bg-slate-50"
            onClick={() => {
              setMoreOpen(false);
              setTab('familiares');
            }}
          >
            <UsersIcon className="h-5 w-5 text-slate-400" />
            Administrar familiares
          </button>
          <button
            type="button"
            className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-medium text-slate-800 hover:bg-slate-50"
            onClick={() => {
              setMoreOpen(false);
              setTab('historial');
            }}
          >
            <DocumentTextIcon className="h-5 w-5 text-slate-400" />
            Historial
          </button>
          <button
            type="button"
            className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-medium text-slate-800 hover:bg-slate-50"
            onClick={() => {
              setMoreOpen(false);
              handleDownloadStatement();
            }}
          >
            <DocumentTextIcon className="h-5 w-5 text-slate-400" />
            Estado de cuenta
          </button>
        </div>
      </BottomSheet>
    </div>
  );
};

export default ClientDetailPage;
